import React, { useEffect, useState } from 'react';
import { Modal, Typography, Button } from 'antd';
import useTranslation from 'utils/hooks/useTranslation';
import { useDispatch, useSelector } from 'react-redux';
import { ROUTE_CREDIT_ACCOUNT } from 'constants.js';
import { useHistory } from 'react-router-dom';
import { CREDIT_ACCOUNT_FAQ_LINK } from 'constants/creditAccount.js';
import {
  AMPLITUDE_EVENT_TYPES,
  AMPLITUDE_EVENT_SOURCES,
  AMPLITUDE_EVENT_BUTTONS,
} from 'constants/amplitude';
import sessionStorageUtils from 'utils/sessionStorageUtils.js';
import useAmplitude from 'utils/hooks/useAmplitude';

import styles from './style.module.scss';

const CreditAccountInfoModal = () => {
  const { translateText } = useTranslation();
  const { sendAmplitudeEvent } = useAmplitude();
  const history = useHistory();
  const { creditAccountMigrationConfig, currentCorporation } = useSelector(state => state.user);
  const dispatch = useDispatch();

  const [isModalInitialized, setIsModalInitialized] = useState(false);
  const [isCreditAccountInfoModalDisplayed, setIsCreditAccountInfoModalDisplayed] = useState(false);

  const corporationId = currentCorporation?.id;
  const creditAccountModalSessionKey =
    !corporationId || corporationId === 'RestrictedCorporateClientProspect'
      ? null
      : `isCreditAccountInfoModalDisplayed-${corporationId}`;

  useEffect(() => {
    if (creditAccountModalSessionKey) {
      const isModalSeenInSession = sessionStorageUtils.get(creditAccountModalSessionKey);
      if (!isModalSeenInSession) {
        setIsCreditAccountInfoModalDisplayed(false);
        if (creditAccountMigrationConfig?.isCreditAccountInfoModalVisible) {
          sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.creditAccountsInfoModalViewed);
          setIsModalInitialized(true);
        }
      }
    } else {
      setIsModalInitialized(false);
    }
  }, [
    sendAmplitudeEvent,
    creditAccountMigrationConfig,
    creditAccountModalSessionKey,
    currentCorporation?.id,
  ]);

  const handleVisibility = ({ changePage }) => {
    sessionStorageUtils.set(creditAccountModalSessionKey, true);
    setIsModalInitialized(false);
    setIsCreditAccountInfoModalDisplayed(true);
    creditAccountMigrationConfig &&
      dispatch({
        type: 'user/SET_STATE',
        payload: {
          creditAccountMigrationConfig: {
            ...creditAccountMigrationConfig,
            isCreditAccountInfoModalVisible: false,
          },
        },
      });

    if (changePage) {
      history.push(ROUTE_CREDIT_ACCOUNT);
      sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.creditAccountsSetUpButtonClicked, {
        source: AMPLITUDE_EVENT_SOURCES.popup,
      });
    } else {
      sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.creditAccountsInfoModalViewed, {
        button: AMPLITUDE_EVENT_BUTTONS.close,
      });
    }
  };

  return (
    isModalInitialized && (
      <Modal
        title={
          <Typography component="h2" className={styles.modalTitle}>
            {translateText('corporateAccounts.creditAccounts.modal.title')}
          </Typography>
        }
        width={600}
        visible={!isCreditAccountInfoModalDisplayed}
        className={styles.modalContainer}
        centered
        closable={false}
        data-testid="creditAccountInfoModal"
        footer={[
          <div key="footer" className={styles.footer}>
            <Button
              key="ok"
              type="primary"
              onClick={() => handleVisibility({ changePage: true })}
              className={`${styles.sharedButton}`}
              data-testid="creditAccountBtn"
            >
              {translateText('corporateAccounts.creditAccounts.modal.cta')}
            </Button>

            <Button
              key="cancel"
              onClick={() => handleVisibility({ changePage: false })}
              className={`${styles.sharedButton} ${styles.cancelButton}`}
            >
              {translateText('corporateAccounts.common.status.close')}
            </Button>
          </div>,
        ]}
      >
        <div className={styles.content}>
          <Typography>
            {translateText('corporateAccounts.creditAccounts.modal.description1')}
          </Typography>
        </div>
        <img
          src="/resources/images/credit_account_info_modal.png"
          alt="Credit account preview"
          className={styles.image}
        />
        <div className={styles.content}>
          <Typography>
            {translateText('corporateAccounts.creditAccounts.modal.description2')}
          </Typography>
          <Typography>
            <span className={styles.bulletPoints}> &bull; </span>
            {translateText('corporateAccounts.creditAccounts.modal.description3')}
          </Typography>
          <Typography>
            <span className={styles.bulletPoints}> &bull; </span>
            {translateText('corporateAccounts.creditAccounts.modal.description4')}
          </Typography>
          <Typography className={styles.link}>
            <a
              href={CREDIT_ACCOUNT_FAQ_LINK}
              rel="noreferrer"
              target="_blank"
              className={styles.link}
              onClick={() =>
                sendAmplitudeEvent(AMPLITUDE_EVENT_TYPES.creditAccountsInfoModalViewed, {
                  button: AMPLITUDE_EVENT_BUTTONS.learnMore,
                })
              }
            >
              {translateText('corporateAccounts.payments.creditAccountBanner.description.cta')}
            </a>
          </Typography>
        </div>
      </Modal>
    )
  );
};

export default CreditAccountInfoModal;
